/**
 * The visual identity of one company: its logo (or a monogram), its name, and
 * its accent colour.
 *
 * This is the single rendering of "which company" in the app. The app-bar
 * control uses it in both of its modes — the scope filter's trigger and options
 * on a collection surface, the record's own company on a detail page — so the
 * cue looks identical wherever the question is answered.
 *
 * The accent is published as a scoped `--accent-company` custom property on the
 * wrapper rather than read from the document root, because a dropdown renders
 * several companies at once and each option needs its own colour. Children then
 * use the ordinary Tailwind `company-accent` utilities.
 *
 * Per STYLE_GUIDE § Theme & Colors the accent carries indication only: the
 * swatch, a wash and a border. The company NAME stays `text-foreground` — a
 * brand hex is pinned for the 3:1 non-text bar, not the 4.5:1 text bar.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { BidiText } from '../common/BidiText';
import { useResolvedThemeOptional } from '../../contexts/ThemeContext';
import { getCompanyAccentHsl } from '../../utils/color';
import { fileApi } from '../../files/services/fileApi';

/**
 * Chrome shared by BOTH arms of the app-bar company control — the scope filter
 * on a collection surface and the record's own company on a detail page.
 *
 * Single-sourced precisely because the two arms occupy the same slot: the whole
 * point of the control is that the reader does not have to notice which mode it
 * is in to read "which company", so a divergence in height, padding or border
 * would be a defect rather than a style nit.
 */
export const COMPANY_CONTROL_TRIGGER_CLASS =
  'h-9 w-auto max-w-[14rem] justify-start gap-2 border-border/60 bg-background/60 text-sm font-medium';

export interface CompanyIdentityCompany {
  id: string;
  name: string;
  branding_color?: string | null;
  logo_file_id?: string | null;
}

export interface CompanyIdentityProps {
  /**
   * The company to render. `null` means the record has no owning company (a
   * legitimate state for an S? entity) and renders the unassigned form;
   * `undefined` means the company could not be resolved — a record reached by
   * deep link outside the user's `accessible_company_ids` — and renders the
   * same neutral form, because the name is genuinely unknown.
   */
  company: CompanyIdentityCompany | null | undefined;
  /** `sm` for dropdown options and dense rows, `md` for the app-bar trigger. */
  size?: 'sm' | 'md';
  /** Hide the name and render the swatch/logo alone (very tight toolbars). */
  iconOnly?: boolean;
  /**
   * Render as if this theme were active, instead of the one that is.
   *
   * ONLY for the admin colour preview, which shows both themes side by side by
   * putting a `dark` class on one wrapper. That flips the CSS variables but not
   * `useThemeMode()`, so without this the "dark" sample renders the LIGHT accent
   * on a dark surface — a preview that shows the admin something they will never
   * see, which is worse than no preview.
   */
  themeOverride?: 'light' | 'dark';
  className?: string;
}

const SIZES = {
  sm: { box: 'h-4 w-4 rounded-[3px]', text: 'text-xs', gap: 'gap-1.5' },
  md: { box: 'h-5 w-5 rounded', text: 'text-sm', gap: 'gap-2' },
} as const;

/**
 * First character of the company name, for the monogram shown when a company
 * has no logo.
 *
 * `Array.from` iterates CODE POINTS, so an astral character (an emoji, some CJK
 * extensions) survives; `name[0]` would slice its surrogate pair in half and
 * render a replacement glyph. Not full grapheme segmentation — `Intl.Segmenter`
 * would give that, but it is outside this project's TS lib target — so a
 * combining mark or a ZWJ sequence still splits. That is an acceptable edge for
 * a one-character monogram with the full name rendered beside it.
 */
function monogramOf(name: string): string {
  const first = Array.from(name.trim())[0];
  return first ? first.toLocaleUpperCase() : '';
}

export function CompanyIdentity({
  company,
  size = 'md',
  iconOnly = false,
  themeOverride,
  className,
}: CompanyIdentityProps) {
  const { t } = useTranslation('common');
  // The OPTIONAL reader: this component renders in the row of ~55 lists, and a
  // theme provider missing from a component test should pick the default theme,
  // not crash the list that contains it.
  const resolvedTheme = useResolvedThemeOptional();
  const [logoFailed, setLogoFailed] = useState(false);
  const sizing = SIZES[size];

  const isDark = (themeOverride ?? resolvedTheme) === 'dark';
  const accent = useMemo(
    () => getCompanyAccentHsl(company?.branding_color, isDark),
    [company?.branding_color, isDark],
  );

  const logoUrl =
    company?.logo_file_id && !logoFailed ? fileApi.getPublicUrl(company.logo_file_id) : null;

  // An unresolved company still gets the slot, so the control keeps its width
  // and the header cluster does not reflow when a record loads.
  const name = company?.name ?? t('multiCompany.unassignedCompany');

  return (
    <span
      className={cn('inline-flex min-w-0 items-center', sizing.gap, className)}
      // A scoped token, not a hex in a styling position: each option in a
      // dropdown carries its own company's accent this way.
      style={accent ? ({ '--accent-company': accent } as React.CSSProperties) : undefined}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex shrink-0 items-center justify-center overflow-hidden border',
          sizing.box,
          accent
            ? 'border-company-accent/50 bg-company-accent/20 text-foreground'
            : 'border-border bg-muted text-muted-foreground',
        )}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            className="h-full w-full object-cover"
            onError={() => setLogoFailed(true)}
          />
        ) : company ? (
          <span className="text-[0.625rem] font-semibold leading-none">
            {monogramOf(company.name)}
          </span>
        ) : (
          <Building2 className="h-3 w-3" />
        )}
      </span>
      {!iconOnly && (
        <span className={cn('min-w-0 truncate text-foreground', sizing.text)}>
          {company ? <BidiText>{name}</BidiText> : name}
        </span>
      )}
    </span>
  );
}
